import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Video, Phone, MessageCircle, Check, ChevronLeft } from 'lucide-react';
import AppHeader from '../../../components/AppHeader';
import BottomNav from '../../../components/BottomNav';
import ErrorPage from '../../../components/ErrorPage';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { formatImageUrl } from '../../../utils/imageUtils';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token') || sessionStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const TYPES = [
  { id: 'chat', icon: <MessageCircle size={20} />, label: 'Chat' },
  { id: 'audio', icon: <Phone size={20} />, label: 'Audio' },
  { id: 'video', icon: <Video size={20} />, label: 'Video' },
];

const AppointmentBooking = () => {
  const { showSuccess, showError } = useNotification();
  const [tab, setTab] = useState('upcoming');
  const [upcoming, setUpcoming] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [step, setStep] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [apptType, setApptType] = useState('video');
  const [reason, setReason] = useState('');
  const [bookLoading, setBookLoading] = useState(false);

  const fetchUpcoming = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/appointments/upcoming');
      setUpcoming(res.data.data || []);
    } catch (err) { setError(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/appointments/history');
      setHistory(res.data.data || []);
    } catch (err) { setError(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'upcoming' || tab === 'new') fetchUpcoming();
    if (tab === 'past') fetchHistory();
  }, [tab]);

  const fetchDoctors = async () => {
    try {
      const res = await api.get('/doctors/approved');
      setDoctors(res.data.data || []);
    } catch { showError('Failed to load doctors'); }
  };

  useEffect(() => { if (step >= 0 && tab === 'new') fetchDoctors(); }, [tab]);

  const fetchSlots = async (doctorId, date) => {
    if (!doctorId || !date) return;
    try {
      const res = await api.get(`/appointments/available-slots/${doctorId}/${date}`);
      setSlots(res.data.data?.slots || []);
    } catch { setSlots([]); }
  };

  useEffect(() => {
    if (selectedDoctor && selectedDate) fetchSlots(selectedDoctor._id, selectedDate);
  }, [selectedDoctor, selectedDate]);

  const handleBook = async () => {
    if (!selectedDoctor || !selectedDate || !selectedSlot || !apptType) {
      showError('Please complete all steps'); return;
    }
    setBookLoading(true);
    try {
      await api.post('/appointments/book', {
        doctorId: selectedDoctor._id,
        date: selectedDate,
        timeSlot: selectedSlot,
        appointmentType: apptType,
        reason: reason.trim() || undefined,
      });
      showSuccess('Appointment booked!');
      resetBooking();
      fetchUpcoming();
      setTab('upcoming');
    } catch (err) {
      showError(err.response?.data?.message || 'Booking failed');
    } finally {
      setBookLoading(false);
    }
  };

  const resetBooking = () => {
    setStep(0); setSelectedDoctor(null); setSelectedDate('');
    setSlots([]); setSelectedSlot(null); setApptType('video'); setReason('');
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.put(`/appointments/${id}/cancel`);
      showSuccess('Appointment cancelled');
      fetchUpcoming();
    } catch (err) { showError(err.response?.data?.message || 'Cancel failed'); }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const renderApptCard = (appt, showActions = true) => (
    <div key={appt._id} className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <img src={formatImageUrl(appt.doctor?.profileImage) || 'https://via.placeholder.com/48'} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" onError={e => { e.target.src = 'https://via.placeholder.com/48'; }} />
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium truncate">Dr. {appt.doctor?.name}</h4>
          <p className="text-xs text-gray-400">{appt.doctor?.specialization}</p>
          <div className="flex gap-3 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Calendar size={12} />{new Date(appt.date).toLocaleDateString()}</span>
            <span className="flex items-center gap-1"><Clock size={12} />{appt.timeSlot?.start || appt.timeSlot}</span>
          </div>
          <div className="flex gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${appt.appointmentType === 'video' ? 'bg-blue-900/50 text-blue-300' : appt.appointmentType === 'audio' ? 'bg-green-900/50 text-green-300' : 'bg-purple-900/50 text-purple-300'}`}>{appt.appointmentType}</span>
            {appt.status && <span className={`text-xs px-2 py-0.5 rounded font-medium ${appt.status === 'scheduled' ? 'bg-green-900/50 text-green-300' : appt.status === 'cancelled' ? 'bg-red-900/50 text-red-300' : 'bg-gray-700 text-gray-400'}`}>{appt.status}</span>}
          </div>
        </div>
      </div>
      {showActions && appt.status === 'scheduled' && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => handleCancel(appt._id)} className="flex-1 bg-yellow-400/20 text-yellow-400 py-2 rounded-lg text-xs font-medium">Cancel</button>
        </div>
      )}
      {appt.reason && <p className="text-xs text-gray-500 mt-2 italic">{appt.reason}</p>}
    </div>
  );

  if (error) return <ErrorPage message={error} onRetry={tab === 'past' ? fetchHistory : fetchUpcoming} />;

  return (
    <div className="bg-dark min-h-screen flex flex-col">
      <AppHeader title="Appointments" />
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="flex gap-2 mb-4">
          {['upcoming', 'past', 'new'].map(t => (
            <button key={t} onClick={() => { setTab(t); if (t !== 'new') resetBooking(); }} className={`px-4 py-2 rounded-lg text-sm font-medium flex-1 text-center capitalize ${tab === t ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}>{t === 'new' ? '+ New' : t}</button>
          ))}
        </div>

        {tab === 'new' ? (
          <div>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 text-gold text-sm font-medium mb-4"><ChevronLeft size={16} /> Back</button>
            )}

            {step === 0 && (
              <div>
                <h3 className="text-gold font-bold mb-3">Select Doctor</h3>
                <input aria-label="Search doctors" className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none mb-3" placeholder="Search doctors..." />
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {doctors.map(doc => (
                    <button key={doc._id} onClick={() => { setSelectedDoctor(doc); setStep(1); }} aria-label={`Select Dr. ${doc.name}`} className={`w-full bg-gray-800 rounded-xl p-3 border text-left transition ${selectedDoctor?._id === doc._id ? 'border-gold bg-gray-700' : 'border-gray-700'}`}>
                      <div className="flex items-center gap-3">
                        <img src={formatImageUrl(doc.profileImage) || 'https://via.placeholder.com/40'} alt="" className="w-10 h-10 rounded-full object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/40'; }} />
                        <div>
                          <p className="text-white text-sm font-medium">Dr. {doc.name}</p>
                          <p className="text-xs text-gray-400">{doc.specialization} {doc.experience ? `· ${doc.experience}yrs` : ''}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                  {doctors.length === 0 && <p className="text-center text-gray-500 py-4">No doctors available</p>}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h3 className="text-gold font-bold mb-3">Select Date</h3>
                <input aria-label="Select date" type="date" min={todayStr} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none mb-4" />
                {selectedDate && (
                  <>
                    <h3 className="text-gold font-bold mb-3">Available Slots</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.length > 0 ? slots.map((slot, i) => (
                        <button key={i} onClick={() => { setSelectedSlot(slot); setStep(2); }} aria-label={`Time slot ${slot.start || slot}`} aria-pressed={selectedSlot === slot} className={`p-3 rounded-xl border text-sm transition ${selectedSlot === slot ? 'bg-gold text-dark border-gold font-bold' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gold'}`}>
                          {slot.start || slot}
                        </button>
                      )) : <p className="col-span-3 text-center text-gray-500 py-4">No slots available for this date</p>}
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <h3 className="text-gold font-bold mb-3">Appointment Type</h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {TYPES.map(t => (
                    <button key={t.id} onClick={() => setApptType(t.id)} aria-label={`${t.label} appointment`} aria-pressed={apptType === t.id} className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition ${apptType === t.id ? 'bg-gold text-dark border-gold font-bold' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>
                      {t.icon}
                      <span className="text-xs">{t.label}</span>
                    </button>
                  ))}
                </div>
                <div>
                  <label htmlFor="reason" className="text-xs text-gray-400 mb-1 block font-medium">Reason / Symptoms</label>
                  <textarea id="reason" value={reason} onChange={e => setReason(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none min-h-[80px]" placeholder="Optional: describe your symptoms..." />
                </div>
                <button onClick={handleBook} disabled={bookLoading} className="w-full bg-gold text-dark py-4 rounded-xl font-bold mt-4 disabled:opacity-50 flex items-center justify-center gap-2">
                  {bookLoading ? 'Booking...' : <><Check size={20} /> Confirm Booking</>}
                </button>
              </div>
            )}

            <div className="flex justify-center gap-1 mt-4">
              {[0, 1, 2].map(s => <div key={s} aria-label={`Step ${s + 1} of 3`} className={`w-2 h-2 rounded-full ${step >= s ? 'bg-gold' : 'bg-gray-600'}`} />)}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {loading ? <p className="text-center text-gray-500 py-12">Loading...</p> :
             (tab === 'upcoming' ? upcoming : history).length > 0 ?
             (tab === 'upcoming' ? upcoming : history).map(a => renderApptCard(a, tab === 'upcoming')) :
             <div className="text-center text-gray-500 py-12">
               <Calendar size={48} className="mx-auto mb-3 opacity-30" />
               <p>No {tab} appointments</p>
             </div>
            }
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default AppointmentBooking;
